import { Copy, Eye, EyeSlash } from "iconsax-react"
import PropTypes from "prop-types"
import { useState } from "react"
import { useLoaderData } from "react-router-dom"
import { toast } from "react-toastify"
import instance from "../../../../../axios/instance"
import { getToken } from "../../../../../utils/token"

export default function ApiKeysPage() {

    const { apiKeys } = useLoaderData()


    return (
        <div className="px-7 py-5 w-full">
            {
                apiKeys.map((key, index) => (
                    <div key={index}>
                        <div className="mb-2 text-[13px] font-medium text-gray-700">{key.pharmacy_name}&apos;s API Key</div>
                        <APIKeyInput
                            api_key={key.api_key}
                            webhook_url={key.webhook_url || ""}
                            webhook_secret={key.webhook_secret || ""}
                        />
                    </div>
                ))
            }
        </div>
    )
}

function isValidHttpsUrl(value) {
    try {
        const parsed = new URL(String(value || "").trim())
        return parsed.protocol === "https:"
    } catch (error) {
        return false
    }
}

function APIKeyInput({ api_key, webhook_url, webhook_secret }) {
    const [viewKey, setViewKey] = useState(false)
    const [viewSecret, setViewSecret] = useState(false)
    const [webhookUrl, setWebhookUrl] = useState(webhook_url)
    const [webhookSecret, setWebhookSecret] = useState(webhook_secret)
    const [saving, setSaving] = useState(false)
    const handleSaveWebhook = async () => {
        const trimmedWebhookUrl = webhookUrl.trim()
        const trimmedWebhookSecret = webhookSecret.trim()

        if (!trimmedWebhookUrl) {
            toast.error("Webhook URL is required")
            return
        }

        if (!trimmedWebhookSecret) {
            toast.error("Webhook secret is required")
            return
        }

        if (!isValidHttpsUrl(trimmedWebhookUrl)) {
            toast.error("Webhook URL must be a valid HTTPS URL")
            return
        }

        const token = getToken()
        setSaving(true)

        try {
            await instance.put(
                "/doctor/api_keys/webhook",
                {
                    webhookUrl: trimmedWebhookUrl,
                    webhookSecret: trimmedWebhookSecret,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            )
            toast.success("Webhook configuration saved")
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to save webhook configuration")
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="mb-8">
            <div className="w-full flex border divide-x select-none rounded-[4px]">
                <input className="flex-1 select-none px-2 text-[14px]" disabled type={!viewKey ? "password" : "text"} value={api_key} />
                <button onClick={() => setViewKey(!viewKey)} className="p-2 hover:bg-gray-200 bg-gray-100">
                    {!viewKey ? <Eye size={18} /> : <EyeSlash size={18} />}
                </button>
                <button onClick={() => {
                    navigator.clipboard.writeText(api_key)
                    toast.success("API Key copied")
                }} className="p-2 hover:bg-gray-200 bg-gray-100">
                    <Copy size={18} />
                </button>
            </div>
            <div className="mt-4">
                <div className="mb-2 text-[13px] font-medium text-gray-700">Webhook URL</div>
                <div className="flex gap-3">
                    <input
                        className="flex-1 rounded-[4px] border px-3 py-2 text-[14px] outline-none focus:border-primary"
                        type="url"
                        placeholder="https://your-domain.com/webhook"
                        value={webhookUrl}
                        onChange={(e) => setWebhookUrl(e.target.value)}
                    />
                </div>
                <div className="mt-2 text-[12px] text-gray-500">
                    Webhook URL must use HTTPS.
                </div>
            </div>
            <div className="mt-4">
                <div className="mb-2 text-[13px] font-medium text-gray-700">Webhook Secret</div>
                <div className="flex gap-3">
                    <div className="flex flex-1 overflow-hidden rounded-[4px] border divide-x">
                        <input
                            className="flex-1 px-3 py-2 text-[14px] outline-none"
                            type={viewSecret ? "text" : "password"}
                            placeholder="Enter shared webhook secret"
                            value={webhookSecret}
                            onChange={(e) => setWebhookSecret(e.target.value)}
                        />
                        <button
                            type="button"
                            onClick={() => setViewSecret(!viewSecret)}
                            className="bg-gray-100 p-2 hover:bg-gray-200"
                        >
                            {!viewSecret ? <Eye size={18} /> : <EyeSlash size={18} />}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                navigator.clipboard.writeText(webhookSecret)
                                toast.success("Webhook secret copied")
                            }}
                            className="bg-gray-100 p-2 hover:bg-gray-200"
                        >
                            <Copy size={18} />
                        </button>
                    </div>
                </div>
                <div className="mt-2 text-[12px] text-gray-500">
                    Use the same secret on your server to verify webhook signatures.
                </div>
                <button
                    type="button"
                    disabled={saving}
                    onClick={handleSaveWebhook}
                    className="mt-4 rounded-[4px] bg-primary px-4 py-2 text-[14px] font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {saving ? "Saving..." : "Save"}
                </button>
            </div>
        </div>
    )
}

APIKeyInput.propTypes = {
    api_key: PropTypes.string.isRequired,
    webhook_url: PropTypes.string,
    webhook_secret: PropTypes.string,
}
